// 抽奖转盘模块 Hooks

import { useCallback, useEffect, useMemo } from "react";
import { useRequest } from "ahooks";
import { throttle } from "lodash-es";
import {
  useWheelStore,
  loadWheelConfig,
  loadWheelSpinCount,
  clearWheelSpinCount,
} from "../stores";
import { useUserDataFlattened } from "../../user/stores";
import { useEvent } from "@/hooks/useEvent";
import { wheelApi } from "../api";
import type { WheelConfig, WheelUserInfo, WheelPrizeRecord } from "../types";
import { triggerPopup } from "../../../utils/popupTriggers";
import { PROXY_ACTIVATED_EVENT } from "../../domainGuard";
import { POPUP_CONFIGS } from "../../../config/popupConfig";

/**
 * 获取转盘配置（首次使用时自动加载）
 * @returns 当前启用的转盘配置；未启用/未加载完成时为 null
 */
export function useWheelConfig(): WheelConfig | null {
  const config = useWheelStore(state => state.config);

  useEffect(() => {
    void loadWheelConfig();
  }, []);

  // 域名容灾软恢复：本 hook 经 PromotionalBanner / EventCenter 挂载（不在 App 的延迟
  // 挂载闸门内），转盘配置属于首波请求。
  // 主域名被封时这一波会失败，而 loadWheelConfig 失败后不置 isLoaded、也没有自动重试，
  // 转盘入口（浮标/banner）会整场缺失。代理切到备用域名后补一次。
  // 幂等：已加载过直接返回缓存、有在飞请求则复用，不会产生多余请求。
  useEffect(() => {
    const onProxyActivated = () => {
      void loadWheelConfig();
    };
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, []);

  return config;
}

export interface WheelEntryState {
  /** 当前启用的转盘；未启用时为 null */
  wheel: WheelConfig | null;
  /** 首页主banner位入口（HOMEPAGE_MAIN_BANNER） */
  showMainBanner: boolean;
  /** 活动列表页banner入口（ACTIVITY_LIST_BANNER） */
  showListBanner: boolean;
  /** 首页右下角浮动图标入口（FLOATING_ICON，admin 侧强制开启） */
  showFloatIcon: boolean;
  /** 主banner图片 fileId（取自 displayConfig.mainBanner） */
  mainBannerImage: string;
  /** 列表banner图片 fileId（取自 displayConfig.listBanner） */
  listBannerImage: string;
  /** 浮动图标图片 fileId（取自 displayConfig.floatIcon） */
  floatIconImage: string;
}

/**
 * 转盘活动入口展示状态
 * @description 与 admin/后端一致：展示配置存于 displayConfig，activityEntry 控制各入口是否展示，
 *   对应图片非空才真正渲染。后端原样下发 displayConfig，由前端按 activityEntry 判断。
 */
export function useWheelEntry(): WheelEntryState {
  const wheel = useWheelConfig();

  return useMemo(() => {
    // /wheel/config 只返回 enabled=true 的转盘，这里再防御一次
    const active = !!wheel && wheel.enabled !== false;
    const dc = wheel?.displayConfig;
    const entry = dc?.activityEntry ?? [];
    const mainBannerImage = dc?.mainBanner ?? "";
    const listBannerImage = dc?.listBanner ?? "";
    const floatIconImage = dc?.floatIcon ?? "";
    return {
      wheel: active ? wheel : null,
      showMainBanner:
        active && entry.includes("HOMEPAGE_MAIN_BANNER") && !!mainBannerImage,
      showListBanner:
        active && entry.includes("ACTIVITY_LIST_BANNER") && !!listBannerImage,
      showFloatIcon:
        active && entry.includes("FLOATING_ICON") && !!floatIconImage,
      mainBannerImage,
      listBannerImage,
      floatIconImage,
    };
  }, [wheel]);
}

/**
 * 转盘入口统一点击处理（三个入口共用）：打开转盘活动弹窗
 */
export function useWheelEntryClick() {
  return useCallback(() => {
    triggerPopup(POPUP_CONFIGS.LUCKY_WHEEL_ACTIVITY.id);
  }, []);
}

/** RECHARGE_SUCCESS 后延迟补拉：后端可能在 WS 推送之后才落次数（同 useUserEvents 补拉惯例） */
const RECHARGE_REFRESH_DELAY_MS = 450;

/**
 * balance 推送节流合并后的次数重拉（模块级共享：useWheelSpinCount 可能多处挂载，
 * 节流窗口必须跨实例生效）。投注结算可高频到达，且监听方是全站在线投注用户，
 * 请求量随并发人数线性放大——红点属增强展示、容忍半分钟延迟，窗口取 30s
 * 压低对 /wheel/info 的放大。leading:false（仅 trailing）：推送到达后延至窗口末
 * 再拉，既给后端落次数留时间（同 RECHARGE_SUCCESS 延迟补拉的动机），
 * 也保证一轮连续投注的最终状态被拉到。
 */
const throttledLoadSpinCount = throttle(
  () => {
    void loadWheelSpinCount();
  },
  30_000,
  { leading: false }
);

/**
 * 用户剩余可抽奖次数（全局共享，供入口红点用）
 * @param enabled 转盘入口实际展示时才请求，避免无谓调用
 * @description 登录后拉取、退出登录清零；转盘弹窗内抽奖/刷新会通过
 *   setWheelSpinCount 实时镜像。站内实时性靠三个触发源，无需刷新页面：
 *   WS RECHARGE_SUCCESS（充值型转盘到账即得次数）、WS balance（投注结算，
 *   仅投注型转盘监听）、visibilitychange 回前台（第三方支付页/切后台返回）
 */
export function useWheelSpinCount(enabled: boolean): number {
  const spinCount = useWheelStore(state => state.spinCount);
  const { isAuthenticated } = useUserDataFlattened();
  const active = enabled && isAuthenticated;

  useEffect(() => {
    if (!enabled) return;
    if (!isAuthenticated) {
      clearWheelSpinCount();
      return;
    }
    void loadWheelSpinCount();
  }, [enabled, isAuthenticated]);

  // 充值到账：充值型转盘次数随即增加，延迟补拉一次
  useEvent("event", (data: unknown) => {
    if (!active) return;
    const event = (data as { data?: { event?: string } })?.data?.event;
    if (event !== "RECHARGE_SUCCESS") return;
    window.setTimeout(() => {
      void loadWheelSpinCount();
    }, RECHARGE_REFRESH_DELAY_MS);
  });

  // 余额变动（投注结算/派彩）：仅投注型转盘次数随累计投注增加。充值型转盘
  // 次数不受投注影响、只走 RECHARGE_SUCCESS，不拉——避免全站 balance 推送
  // 被放大成对 /wheel/info 的持续无效轮询
  useEvent("balance", () => {
    if (!active) return;
    if (useWheelStore.getState().config?.sendRecordConf?.enableRecharge) return;
    throttledLoadSpinCount();
  });

  // 回到前台（第三方支付页/切后台返回）：补拉一次，兜底 WS 断连期间的变化
  useEffect(() => {
    if (!active) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadWheelSpinCount();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [active]);

  return spinCount;
}

/**
 * 用户剩余抽奖次数与积分
 * @param ready 弹窗打开后再请求，避免无谓调用
 */
export function useWheelInfo(ready: boolean) {
  return useRequest<WheelUserInfo, []>(() => wheelApi.getInfo(), {
    ready,
    refreshDeps: [ready],
  });
}

/**
 * 当前登录用户本人的中奖记录（Rewards Record 用）
 * @param wheelId 转盘 id；为空时不请求
 */
export function useWheelMyRecords(wheelId?: string) {
  return useRequest<WheelPrizeRecord[], []>(
    () => wheelApi.getMyRecords(wheelId as string),
    {
      ready: !!wheelId,
      refreshDeps: [wheelId],
    }
  );
}
